import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Le `top` d'une barre `sticky` d'écran, pour qu'elle se colle SOUS le menu
 * Orbit — et non dessous au sens propre.
 *
 * POURQUOI DU JS ICI (justification config-first) : il s'agit de MESURER en
 * continu la hauteur réelle d'un élément du DOM (`.orbit-shell-header`, monté
 * par reactive-json après l'écran) et d'en dériver un style. Aucune donnée, donc
 * aucun YAML reactive-json ni aucune capacité Drupal ne l'exprime ; ce n'est pas
 * non plus du CSS (la hauteur dépend du contenu du menu et de la disposition
 * choisie). Et c'est un hook et non un second bloc inline : la logique existait
 * déjà dans `BuilderHeader`, qui la CONSOMME désormais — une seule mécanique
 * pour les deux barres, pas deux à tenir synchrones.
 *
 * Le chrome de l'appli rend son menu horizontal en `sticky top-0 z-50` : toute
 * barre d'écran en `sticky top-0` avec un z-index inférieur se colle au même 0
 * et disparaît derrière lui dès qu'on défile. Le décalage ne peut pas être une
 * constante :
 *
 *  - la disposition est un CHOIX de l'utilisateur (menu horizontal ou sidebar,
 *    classe de `<body>`) : en sidebar le menu est en `display: none`, hauteur
 *    nulle, et un `top-16` en dur laisserait une bande blanche ;
 *  - la hauteur du menu dépend de son contenu, et la bascule header/sidebar
 *    sous `lg` est une media query — que `ResizeObserver` seul ne voit pas ;
 *  - le menu est monté par reactive-json APRÈS les composants React de l'écran
 *    (il charge son YAML) : une mesure unique au montage trouve souvent `null`.
 *
 * D'où : `MutationObserver` jusqu'à ce que le menu existe, `ResizeObserver`
 * dessus ensuite (il se déclenche aussi au passage en `display: none`), plus un
 * écouteur `resize` pour la bascule de disposition.
 *
 * Le décalage ne vaut que si la barre défile avec le DOCUMENT. Embarquée dans
 * un conteneur défilant — un conteneur qui commence DÉJÀ sous le menu —, `top`
 * s'y compte depuis le haut de ce conteneur et la hauteur du menu s'ajouterait
 * en pur blanc : on cherche donc le premier ancêtre qui défile, et l'offset est
 * alors nul.
 *
 * La HAUTEUR de la barre elle-même est rendue en troisième valeur : ce qui se
 * colle SOUS elle (un panneau latéral à défilement propre) a besoin du même
 * calcul, et la mesurer une seconde fois ailleurs donnerait deux réponses
 * divergentes dès que la barre passe à la ligne.
 *
 * Usage :
 *   const [headerRef, stickyTop, headerHeight] = useOrbitStickyTop()
 *   <header ref={headerRef} className="sticky z-30 …" style={{ top: stickyTop }}>
 *
 * @returns {[(el: HTMLElement|null) => void, number, number]}
 *   La ref (de rappel) à poser sur la barre, le `top` à lui donner, et sa
 *   hauteur rendue.
 */
export const useOrbitStickyTop = () => {
  const ref = useRef(null)
  const [stickyTop, setStickyTop] = useState(0)
  const [height, setHeight] = useState(0)
  // Une ref de RAPPEL, et non un simple objet : l'écran qui consomme ce hook
  // peut ne monter sa barre qu'après un chargement (l'éditeur de contenu
  // commence par un spinner). Une mesure faite au seul montage du composant
  // trouverait alors `null` et ne reviendrait jamais.
  const [node, setNode] = useState(null)
  const setRef = useCallback((el) => {
    ref.current = el
    setNode(el)
  }, [])

  // La hauteur de la barre : elle change quand la barre passe à la ligne
  // (fenêtre étroite, assistant ouvert en colonne) — donc un ResizeObserver, et
  // non une mesure ponctuelle.
  useEffect(() => {
    if (!node) return undefined
    const obs = new ResizeObserver(() => {
      const h = node.getBoundingClientRect().height
      setHeight(prev => (prev === h ? prev : h))
    })
    obs.observe(node)
    return () => obs.disconnect()
  }, [node])

  useEffect(() => {
    let resizeObs = null
    let mutationObs = null

    const apply = (nav) => {
      const scroller = (() => {
        let el = ref.current?.parentElement
        while (el && el !== document.body) {
          const oy = getComputedStyle(el).overflowY
          if (oy === 'auto' || oy === 'scroll') return el
          el = el.parentElement
        }
        return null
      })()
      const h = !scroller && nav && getComputedStyle(nav).display !== 'none'
        ? nav.getBoundingClientRect().height
        : 0
      setStickyTop(prev => (prev === h ? prev : h))
    }

    const attach = () => {
      const nav = document.querySelector('.orbit-shell-header')
      if (!nav) return false
      apply(nav)
      resizeObs = new ResizeObserver(() => apply(nav))
      resizeObs.observe(nav)
      return true
    }

    if (!attach()) {
      mutationObs = new MutationObserver(() => {
        if (attach()) { mutationObs.disconnect(); mutationObs = null }
      })
      mutationObs.observe(document.body, { childList: true, subtree: true })
    }

    const onResize = () => apply(document.querySelector('.orbit-shell-header'))
    window.addEventListener('resize', onResize)
    return () => {
      resizeObs?.disconnect()
      mutationObs?.disconnect()
      window.removeEventListener('resize', onResize)
    }
    // `node` : la recherche d'un ancêtre défilant part de la barre elle-même.
    // Montée après coup (écran qui commence par un spinner), elle doit rejouer
    // la mesure — sans quoi l'offset resterait celui calculé hors du DOM.
  }, [node])

  return [setRef, stickyTop, height]
}
